import { memo, useRef } from 'react';
import { CircularProgress, IconButton, Typography } from '@material-ui/core';
import {
  AddRepresentativesDialog,
  AddRepresentativesDialogContent,
  AddRepresentativesDialogCloseContainer,
  AddRepresentativesDialogTitleContainer,
  AddRepresentativesForm,
  AddRepresentativesAction,
  AddRepresentativesButton,
} from './AddRepresentativesModal.styles';
import { AddProfileIcon } from '@room-match/shared-ui-components'; // Remove Close Icon
import { useAddRepresentativesData } from '../../hooks/useAddRepresentativesData';
import { useAddRepresentativesFormik } from '../../hooks/useAddRepresentativesFormik';
import CloseIcon from 'shared/components/Icon/CloseIcon';
import AddRepresentativesFormList from '../AddRepresentativesFormList/AddRepresentativesFormList';
import AddRepresentativesFormListAgency from '../AddRepresentativesFormListAgency/AddRepresentativesFormListAgency';

type AddRepresentativesModalProps = {
  open: boolean;
  handleOnClose: VoidFunction;
  hasFreelance?: boolean;
};

const AddRepresentativesModal = ({ open, handleOnClose, hasFreelance = false }: AddRepresentativesModalProps) => {
  // Use hooks to get the data that is needed for creating a Representatives
  const { userCountry, medium_id } = useAddRepresentativesData();

  // Use hooks to setup the data that will be send in backend
  const {
    formikForm,
    agencyRequiredField,
    allowedToSave,
    creationLoading,
    handleSubmit,
    handleSetFieldValue,
    resetAddFormValues,
  } = useAddRepresentativesFormik(userCountry, hasFreelance, medium_id, handleOnClose);

  /* This ref force the AddRepresentativesFormListAgencyTypeField.tsx to re-render and clear the inputs  */
  const countRef = useRef(0);

  return (
    <AddRepresentativesDialog
      fullScreen
      open={open}
      onClose={handleOnClose}
      required={agencyRequiredField?.code_required || agencyRequiredField?.division_required}
    >
      <AddRepresentativesDialogContent>
        {/* Close Container */}
        <AddRepresentativesDialogCloseContainer>
          <IconButton onClick={handleOnClose} style={{ fontSize: 18 }}>
            <CloseIcon fontSize="inherit" />
          </IconButton>
        </AddRepresentativesDialogCloseContainer>

        {/* Title Container */}
        <AddRepresentativesDialogTitleContainer>
          <AddProfileIcon style={{ width: 40, height: 40 }} />
          <Typography variant="h5">Add a new Representative</Typography>
        </AddRepresentativesDialogTitleContainer>

        {/* Form Container */}
        <AddRepresentativesForm
          onSubmit={(e) => {
            e.preventDefault();
            handleSubmit();
          }}
        >
          {/* Form that is Common, Representation Type, Country and Profile Type */}
          <AddRepresentativesFormList
            representation_type={formikForm?.values.representation_type}
            profile_type={formikForm.values.profile_type}
            country={formikForm.values.country}
            hasFreelance={hasFreelance}
            handleSetFieldValue={handleSetFieldValue}
          />
          {/* Form that is optional if representation type is Agency Representation, Agency Manager, division and division codes */}
          {formikForm.values.representation_type === 'Agency Representation' ? (
            <AddRepresentativesFormListAgency
              /* 
              This key is important because every agency in country is different, this force to re-render when the country is changed
              the countRef.current purpose is to re-render this field when the clear form is clicked
              */
              key={`${formikForm?.values.country}-${countRef.current}`}
              country={formikForm?.values.country}
              access_code={formikForm?.values.access_code}
              division_ids={formikForm?.values.division_ids}
              code_required={agencyRequiredField.code_required}
              division_required={agencyRequiredField.division_required}
              handleSetFieldValue={handleSetFieldValue}
            />
          ) : null}
          {/* Form Actions */}
          <AddRepresentativesAction>
            <AddRepresentativesButton
              type="submit"
              variant="contained"
              color="primary"
              disabled={creationLoading || !allowedToSave}
            >
              {creationLoading ? <CircularProgress size={18} /> : 'Add Representative'}
            </AddRepresentativesButton>
            <AddRepresentativesButton
              onClick={() => {
                resetAddFormValues();
                countRef.current += 1;
              }}
              variant="contained"
              color="secondary"
            >
              Reset
            </AddRepresentativesButton>
          </AddRepresentativesAction>
        </AddRepresentativesForm>
      </AddRepresentativesDialogContent>
    </AddRepresentativesDialog>
  );
};

export default memo(AddRepresentativesModal);
